import React, { useEffect, useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import toast from 'react-hot-toast';
import { apiClient, getErrorMessage } from '../packages/api-client';
import { Spinner } from '../packages/ui';
import { useAuth } from '../packages/auth-client';
import { KaziAtlasLogo } from '../components/KaziAtlasLogo';
import { SetPasswordForm } from '../components/SetPasswordForm';
import { usePageTitle } from '../hooks/usePageTitle';
import { useTheme } from '../hooks/useTheme';

interface InvitationView {
    email: string;
    first_name: string;
    last_name: string;
    tenant_name: string;
    inviter_name: string;
}

/**
 * OrgInviteAcceptPage — public page at /invitations/org/:token. Looks up the
 * invitation (GET /user-invitations/:token), shows context, collects a
 * password, POSTs to /user-invitations/:token/accept, then bounces the
 * invitee to /login so they go through OTP like any other sign-in.
 */
export function OrgInviteAcceptPage() {
    usePageTitle('Join workspace');
    const { theme } = useTheme();
    const { token = '' } = useParams<{ token: string }>();
    const navigate = useNavigate();
    const { user, isLoading: authLoading, logout } = useAuth();

    const [view, setView] = useState<InvitationView | null>(null);
    const [loadError, setLoadError] = useState<string | null>(null);
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        if (!token) {
            setLoadError('This invitation link is missing its token.');
            setLoading(false);
            return;
        }
        let cancelled = false;
        apiClient.get<{ data: InvitationView }>(`/user-invitations/${token}`)
            .then((r) => {
                if (cancelled) return;
                setView(r.data.data);
            })
            .catch((err) => {
                if (cancelled) return;
                setLoadError(getErrorMessage(err));
            })
            .finally(() => { if (!cancelled) setLoading(false); });
        return () => { cancelled = true; };
    }, [token]);

    async function handleSubmit(password: string) {
        await apiClient.post(`/user-invitations/${token}/accept`, { password });
        toast.success(`Welcome to ${view?.tenant_name ?? 'your workspace'} — please sign in.`);
        navigate('/login', { replace: true });
    }

    if (loading || authLoading) {
        return (
            <div className="login-container">
                <div className="login-card">
                    <div style={{ marginBottom: '1rem' }}><KaziAtlasLogo size={46} onLight={theme === 'light'} /></div>
                    <div style={{ display: 'flex', justifyContent: 'center', padding: '1.5rem 0' }}>
                        <Spinner size="lg" />
                    </div>
                </div>
            </div>
        );
    }

    if (loadError) {
        return (
            <div className="login-container">
                <div className="login-card">
                    <div style={{ marginBottom: '1rem' }}><KaziAtlasLogo size={46} onLight={theme === 'light'} /></div>
                    <h1 className="login-title">Invitation not available</h1>
                    <p className="login-subtitle">{loadError}</p>
                    <Link
                        to="/login"
                        className="btn btn-primary form-button"
                        style={{ marginTop: '1rem', display: 'inline-flex', alignItems: 'center', justifyContent: 'center', textDecoration: 'none' }}
                    >
                        Go to sign-in
                    </Link>
                </div>
            </div>
        );
    }

    // Logged in as a different account — interstitial to avoid account
    // confusion. The invitee is expected to be a brand-new user, so if
    // anyone is logged in here at all it's almost always the wrong session.
    if (user && view && user.email !== view.email) {
        return (
            <div className="login-container">
                <div className="login-card">
                    <div style={{ marginBottom: '1rem' }}><KaziAtlasLogo size={46} onLight={theme === 'light'} /></div>
                    <h1 className="login-title">You're signed in as someone else</h1>
                    <p className="login-subtitle">
                        This invitation is for <strong style={{ color: 'var(--ink)' }}>{view.email}</strong>,
                        but you're currently signed in as <strong style={{ color: 'var(--ink)' }}>{user.email}</strong>.
                        Sign out to continue.
                    </p>
                    <button
                        type="button"
                        className="btn btn-primary form-button"
                        style={{ marginTop: '1rem' }}
                        onClick={async () => {
                            await logout({ redirect: false });
                            // Stay on this page so the flow resumes after sign-out.
                            navigate(`/invitations/org/${token}`, { replace: true });
                        }}
                    >
                        Sign out
                    </button>
                </div>
            </div>
        );
    }

    const displayName = `${view?.first_name ?? ''} ${view?.last_name ?? ''}`.trim() || view?.email;

    return (
        <div className="login-container">
            <div className="login-card">
                <div style={{ marginBottom: '1rem' }}><KaziAtlasLogo size={46} onLight={theme === 'light'} /></div>
                <h1 className="login-title">Welcome{view?.first_name ? `, ${view.first_name}` : ''}!</h1>
                <p className="login-subtitle">
                    {view?.inviter_name
                        ? <>{view.inviter_name} invited you to join </>
                        : <>You've been invited to join </>}
                    <strong style={{ color: 'var(--ink)' }}>{view?.tenant_name || 'the workspace'}</strong>
                    {' '}on KaziAtlas. Pick a password below to finish setting up your account
                    {view?.email && <> for <strong style={{ color: 'var(--ink)' }}>{view.email}</strong></>}.
                </p>
                <SetPasswordForm onSubmit={handleSubmit} submitLabel={`Join ${view?.tenant_name || 'workspace'}`} />
                {displayName && (
                    <p style={{ marginTop: '1rem', fontSize: '0.75rem', color: 'var(--ink-muted)', textAlign: 'center' }}>
                        Joining as {displayName}
                    </p>
                )}
            </div>

            <p style={{ textAlign: 'center', marginTop: '1.25rem', fontSize: '0.875rem', color: 'var(--ink-muted)' }}>
                <Link to="/login" style={{ color: 'var(--accent)', fontWeight: 600, textDecoration: 'none' }}>
                    Already have an account? Sign in
                </Link>
            </p>
        </div>
    );
}
